import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RotatingLines } from "react-loader-spinner";
import { useSnackbar } from "notistack";

import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { collection, deleteDoc, doc, getDocs } from "firebase/firestore";

import CategoryManager from "../components/CategoryManager";
import ProductForm from "../components/ProductForm";
import { getFormattedDuration, price } from "../components/utils";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";

const Catalogue = () => {
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [search, setSearch] = useState("");
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();

  // New state for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  useEffect(() => {
    const fetchCatalog = async () => {
      if (currentUser) {
        try {
          const catalogRef = collection(db, `users/${currentUser.uid}/catalog`);
          const snapshot = await getDocs(catalogRef);
          const catalogList = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setCatalog(catalogList);
          setLoading(false);
        } catch (error) {
          enqueueSnackbar(t("catalog.notifications.fetchError") + error.message, {
            variant: "error"
          });
          setLoading(false);
        }
      }
    };

    fetchCatalog();
  }, [currentUser, enqueueSnackbar, t]);

  const handleSearchChange = event => {
    setSearch(event.target.value);
  };

  const handleOpenForm = (product, productId = null) => {
    setSelectedProductId(productId);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedProductId(null);
  };

  const handleSaveProduct = savedProduct => {
    const updatedCatalog = [...catalog];
    const productIndex = updatedCatalog.findIndex(
      product => product.id === savedProduct.id
    );

    if (productIndex >= 0) {
      updatedCatalog[productIndex] = savedProduct;
    } else {
      updatedCatalog.push(savedProduct);
    }

    setCatalog(updatedCatalog);
    handleCloseForm();
  };

  // Modified to open confirmation dialog instead of deleting immediately
  const handleDeleteClick = productId => {
    setProductToDelete(productId);
    setDeleteDialogOpen(true);
  };

  // Actual delete function
  const handleDeleteProduct = async () => {
    try {
      await deleteDoc(doc(db, `users/${currentUser.uid}/catalog`, productToDelete));
      setCatalog(catalog.filter(product => product.id !== productToDelete));
      enqueueSnackbar(t("catalog.notifications.deleteSuccess"), { variant: "success" });
    } catch (error) {
      enqueueSnackbar(t("catalog.notifications.deleteError") + error.message, {
        variant: "error"
      });
    } finally {
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    }
  };

  const filteredCatalog = catalog.filter(
    item =>
      (item.type && item.type.toLowerCase().includes(search.toLowerCase())) ||
      (item.name && item.name.toLowerCase().includes(search.toLowerCase())) ||
      (item.description &&
        item.description.toLowerCase().includes(search.toLowerCase()))
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <RotatingLines
          strokeColor={theme.palette.primary.main}
          strokeWidth="5"
          animationDuration="0.75"
          width="96"
          visible={true}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "100%", mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          background: theme =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(148, 163, 184, 0.14), rgba(71, 85, 105, 0.16))"
              : "#ffffff"
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                mb: 0.5,
                color: theme => theme.palette.mode === "dark" ? "primary.light" : "#0f172a",
                fontWeight: 800
              }}
            >
              {t("catalog.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredCatalog.length} / {catalog.length} prestation(s)
            </Typography>
          </Box>
          {search && (
            <Chip
              label={`Filtre actif: ${search}`}
              variant="outlined"
              onDelete={() => setSearch("")}
              deleteIcon={<CloseIcon />}
              sx={{
                backgroundColor: theme =>
                  theme.palette.mode === "dark"
                    ? "rgba(148, 163, 184, 0.12)"
                    : "rgba(15, 23, 42, 0.04)",
                borderColor: theme => theme.palette.divider
              }}
            />
          )}
        </Stack>
      </Paper>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", md: "center" }}
        sx={{ mb: 2 }}
      >
        <TextField
          variant="outlined"
          placeholder={t("common.search")}
          value={search}
          onChange={handleSearchChange}
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5,
              backgroundColor: theme =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(0, 0, 0, 0.02)"
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenForm(null, null)}
            sx={{
              minWidth: { xs: "100%", md: 210 },
              height: 56,
              borderRadius: 2.5,
              fontWeight: 600,
              backgroundColor: theme =>
                theme.palette.mode === "dark" ? "rgba(148, 163, 184, 0.22)" : "#1f2937",
              color: theme => (theme.palette.mode === "dark" ? "#e2e8f0" : "#f8fafc"),
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme =>
                  theme.palette.mode === "dark" ? "rgba(148, 163, 184, 0.3)" : "#111827",
                boxShadow: "none"
              }
            }}
          >
            {t("catalog.addProduct")}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setCategoryManagerOpen(true)}
            sx={{
              minWidth: { xs: "100%", md: 220 },
              height: 56,
              borderRadius: 2.5,
              fontWeight: 600
            }}
          >
            {t("catalog.manageCategories")}
          </Button>
        </Stack>
      </Stack>

      <TableContainer 
        component={Paper} 
        sx={{ 
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          boxShadow: theme => theme.palette.mode === "dark" 
            ? "0 4px 6px rgba(0, 0, 0, 0.4)"
            : "0 1px 2px rgba(15, 23, 42, 0.06)",
          overflow: "hidden",
          backgroundColor: theme => theme.palette.mode === "dark" 
            ? "background.paper"
            : "#fff"
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: theme => theme.palette.mode === "dark"
                  ? "rgba(148, 163, 184, 0.08)"
                  : "#f8fafc"
              }}
            >
              <TableCell sx={{ 
                fontWeight: 700,
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: theme => theme.palette.mode === "dark" ? "primary.light" : "#64748b",
                py: 2
              }}>{t("catalog.type")}</TableCell>
              <TableCell sx={{ 
                fontWeight: 700,
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: theme => theme.palette.mode === "dark" ? "primary.light" : "#64748b",
                py: 2
              }}>{t("catalog.name")}</TableCell>
              <TableCell sx={{ 
                fontWeight: 700,
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: theme => theme.palette.mode === "dark" ? "primary.light" : "#64748b",
                py: 2
              }}>{t("catalog.description")}</TableCell>
              <TableCell sx={{ 
                fontWeight: 700,
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: theme => theme.palette.mode === "dark" ? "primary.light" : "#64748b",
                py: 2
              }}>{t("catalog.duration")}</TableCell>
              <TableCell sx={{ 
                fontWeight: 700,
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: theme => theme.palette.mode === "dark" ? "primary.light" : "#64748b",
                py: 2
              }}>{t("catalog.price")}</TableCell>
              <TableCell sx={{ 
                fontWeight: 700,
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: theme => theme.palette.mode === "dark" ? "primary.light" : "#64748b",
                py: 2
              }}>{t("catalog.options")}</TableCell>
              <TableCell sx={{ 
                fontWeight: 700,
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: theme => theme.palette.mode === "dark" ? "primary.light" : "#64748b",
                py: 2
              }}>{t("common.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCatalog.map(item => (
              <TableRow 
                key={item.id}
                sx={{
                  transition: "background-color 0.2s",
                  "&:hover": {
                    backgroundColor: theme => theme.palette.mode === "dark"
                      ? "rgba(148, 163, 184, 0.12)"
                      : "#f8fafc"
                  }
                }}
              >
                <TableCell sx={{ fontWeight: 500, py: 1.75 }}>{item.type}</TableCell>
                <TableCell sx={{ py: 1.75, fontWeight: 600 }}>{item.name}</TableCell>
                <TableCell sx={{ py: 1.75 }}>{item.description}</TableCell>
                <TableCell sx={{ py: 1.75 }}>{getFormattedDuration(item.duration)}</TableCell>
                <TableCell sx={{ 
                  py: 1.75,
                  fontWeight: 500,
                  color: theme => theme.palette.mode === "dark" 
                    ? "success.light" 
                    : "success.dark"
                }}>
                  {price(item.price)}
                </TableCell>
                <TableCell sx={{ py: 1.75 }}>{item.options}</TableCell>
                <TableCell sx={{ py: 1.75 }}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title={t("common.edit")}>
                      <IconButton 
                        onClick={() => handleOpenForm(item, item.id)}
                        size="small"
                        sx={{ 
                          color: theme => theme.palette.mode === "dark" 
                            ? "primary.light" 
                            : "#b98908",
                          "&:hover": {
                            backgroundColor: "rgba(212, 164, 17, 0.12)"
                          }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.delete")}>
                      <IconButton 
                        onClick={() => handleDeleteClick(item.id)}
                        size="small"
                        sx={{ color: "error.main" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filteredCatalog.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 6 }}>
                  <Box textAlign="center">
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Aucun résultat dans le catalogue
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Essaie un autre terme ou ajoute une nouvelle prestation.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ProductForm
        open={formOpen}
        handleClose={handleCloseForm}
        productId={selectedProductId}
        userId={currentUser.uid}
        onSave={handleSaveProduct}
      />

      <CategoryManager
        open={categoryManagerOpen}
        handleClose={() => setCategoryManagerOpen(false)}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("catalog.deleteConfirmation.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("catalog.deleteConfirmation.message")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleDeleteProduct} color="error">
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Catalogue;
